import { configureIdentityStorage } from '@hds/identity-client-sdk';

import { createDeviceStorage, createMemoryStorageDriver } from './deviceStorage.js';

/**
 * The ports a host supplies, and where a store inside this package reaches for
 * one.
 *
 * A module-level registry rather than an argument threaded through every store,
 * for the same reason the two API clients are singletons configured by property
 * setters at boot: a Pinia store is constructed by Pinia, not by us, so there is
 * no call site to pass anything to. `configurePorts` is called once from the
 * host's entry point, beside where the transports are configured, and after
 * that a store just asks.
 *
 * Every port has a default that WORKS rather than one that throws. An SDK with
 * nothing configured runs — it forgets what it stored between reloads, has no
 * realtime and fires no notifications — because the alternative is that every
 * store has to check whether its port exists before every call, and one of
 * those checks will be missing.
 */

// `subscribe(eventType, handler) -> unsubscribe` and `send(payload)` — the
// shape the mobile portal's socket client already has, for the same reason the
// storage driver's shape is Capacitor's: a port that makes the existing host
// rewrite itself is a port nobody adopts.
const NO_REALTIME = Object.freeze({
    subscribe: () => () => {},
    send: () => {}
});

const NO_NOTIFICATIONS = Object.freeze({
    notify: () => {}
});

const NO_GEOLOCATION = Object.freeze({
    // Resolving to null rather than rejecting is the same rule the storage port
    // follows: "I don't know where you are" is an answer a caller can act on,
    // and a rejection on a permission-gated read is not.
    currentPosition: async () => null
});

// `trace|debug|info|warn|error|fatal(fields, message)` — the `@frankai/logger`
// interface, which is what both halves of the system already speak.
//
// A logger is a port rather than a module in here for the reason the others
// are: where an entry GOES is the host's business. A phone mirrors to the
// device console and forwards to Datadog; a desktop surface may do neither; a
// test collects into an array. The vocabulary is ours — `http.request`,
// `bid.proposed`, the redaction paths — and stays in this package, on the call
// sites that emit it.
//
// It is also what keeps the logger's NAME honest. `createLogger({ name })`
// stamps every entry with the app that wrote it, and the app is the only thing
// that knows which app it is.
const NO_LOGGING = Object.freeze({
    trace: () => {},
    debug: () => {},
    info: () => {},
    warn: () => {},
    error: () => {},
    fatal: () => {}
});

function defaultPorts() {
    return {
        deviceStorage: createDeviceStorage(createMemoryStorageDriver(), { name: 'memoryDeviceStorage' }),
        realtime: NO_REALTIME,
        notifications: NO_NOTIFICATIONS,
        geolocation: NO_GEOLOCATION,
        logger: NO_LOGGING
    };
}

let ports = defaultPorts();

/**
 * Install the host's ports. Merges rather than replaces, so a host can supply
 * storage at boot and realtime later, and so a test can override one port
 * without restating the rest.
 *
 * @param {Object} supplied
 * @param {Object} [supplied.deviceStorage] Already wrapped by createDeviceStorage.
 * @param {Object} [supplied.realtime]      { subscribe(eventType, handler) -> unsubscribe, send(payload) }
 * @param {Object} [supplied.notifications] { notify(event) }
 * @param {Object} [supplied.geolocation]   { currentPosition() -> { latitude, longitude } | null }
 * @param {Object} [supplied.logger]        { trace|debug|info|warn|error|fatal(fields, message) }
 */
export function configurePorts(supplied = {}) {
    ports = { ...ports, ...supplied };
    // The identity client keeps its own storage seam (it cannot depend up into
    // this port registry). Hand it the same device-storage instance the host
    // just supplied, so the host still wires storage in one place.
    if (supplied.deviceStorage !== undefined) {
        configureIdentityStorage(supplied.deviceStorage);
    }
    return ports;
}

/** Every port, for a store that needs more than one. */
export function usePorts() {
    return ports;
}

export const useDeviceStorage = () => ports.deviceStorage;
export const useRealtime = () => ports.realtime;
export const useNotifications = () => ports.notifications;
export const useGeolocation = () => ports.geolocation;
export const useLogger = () => ports.logger;

/**
 * Put every port back to its default. For tests — a port left installed by one
 * test is the kind of leak that makes the next one pass for the wrong reason.
 */
export function resetPorts() {
    ports = defaultPorts();
    return ports;
}
